// https://react-hook-form.com/advanced-usage#SmartFormComponent

import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import React from 'react'
import {
  DeepPartial,
  FieldErrors,
  FormProvider,
  SubmitHandler,
  UnpackNestedValue,
  useForm,
} from 'react-hook-form'
import { AnyObjectSchema } from 'yup'

type Props<T> = {
  defaultValues: UnpackNestedValue<DeepPartial<T>>
  validationSchema?: AnyObjectSchema
  validateOnChange?: boolean
  revalidateOnChange?: boolean
  skipValidation?: boolean
  onSubmit: SubmitHandler<T>
  onErrorSubmit?: (data: {
    errors: FieldErrors<T>
    data: UnpackNestedValue<T>
  }) => void
  hideDevTool?: boolean
  className?: string
}

export const Form = <T extends Record<string, unknown>>({
  defaultValues,
  validationSchema,
  validateOnChange, // Validate form on value change
  revalidateOnChange = true, // Validate form on value change
  onSubmit,
  onErrorSubmit,
  skipValidation,
  hideDevTool,
  children,
  className,
}: WithChildren<Props<T>>): JSX.Element => {
  const methods = useForm<T>({
    defaultValues,
    mode: validateOnChange ? 'onChange' : 'onSubmit',
    reValidateMode: revalidateOnChange ? 'onChange' : 'onSubmit',
    resolver: validationSchema
      ? yupResolver(validationSchema as AnyObjectSchema)
      : undefined,
  })

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={className}
          onSubmit={methods.handleSubmit(
            onSubmit,
            // Call submit on errorSubmit to avoid validation
            skipValidation
              ? () => onSubmit(methods.getValues())
              : () =>
                  onErrorSubmit
                    ? onErrorSubmit({
                        errors: methods.formState.errors,
                        data: methods.getValues(),
                      })
                    : undefined
          )}
        >
          {children}
        </form>
      </FormProvider>
      {!hideDevTool && (
        <DevTool placement='top-right' control={methods.control} />
      )}
    </>
  )
}
